import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { VideoPlayerModal } from '@/components/ui/VideoPlayerModal';

interface SearchResult {
  chunk_id: string;
  video: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    speaker: string | null;
    source: string;
    source_id: string | null;
    source_url: string | null;
  };
  chunk_index: number;
  content: string;
  start_time: number;
  end_time: number;
  summary: string | null;
  relevance_score: number;
}

interface SearchResponse {
  data: {
    results: SearchResult[];
    query: string;
    total: number;
  };
  meta: {
    page: number;
    per_page: number;
    total_pages: number;
  };
}

interface Category {
  id: string;
  name: string;
  slug: string;
  video_count: number;
}

export function SearchPage() {
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState('');
  const [activeResult, setActiveResult] = useState<SearchResult | null>(null);

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ data: Category[] }>('/categories'),
  });

  const categories = categoriesData?.data ?? [];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['search', searchQuery, activeCategoryId],
    queryFn: () => {
      const params = new URLSearchParams({ q: searchQuery });
      if (activeCategoryId) params.set('category_id', activeCategoryId);
      return api.get<SearchResponse>(`/search?${params.toString()}`);
    },
    enabled: searchQuery.length > 0,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(query.trim());
    setActiveCategoryId(categoryId);
  };

  const formatTimestamp = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const results = data?.data.results ?? [];
  const total = data?.data.total ?? 0;
  const activeCategoryName = activeCategoryId
    ? categories.find((c) => c.id === activeCategoryId)?.name
    : null;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Search</h1>
        <p className="mt-1 text-sm text-gray-500">
          Search across all your video transcripts
        </p>
      </div>

      <form onSubmit={handleSearch} className="mb-8 space-y-3">
        <div className="relative max-w-2xl">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search transcripts..."
            className="input pr-12 text-lg py-3"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-primary-600"
            aria-label="Search"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </button>
        </div>

        <div className="flex max-w-2xl items-center gap-3">
          <label htmlFor="category-filter" className="text-sm font-medium text-gray-700">
            Category
          </label>
          <select
            id="category-filter"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="input flex-1 max-w-xs"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.video_count})
              </option>
            ))}
          </select>
          {categoryId && (
            <button
              type="button"
              onClick={() => {
                setCategoryId('');
                if (searchQuery) setActiveCategoryId('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {error ? (
        <div className="card p-6 border border-red-200 bg-red-50">
          <p className="text-sm text-red-700">
            Search failed: {(error as Error).message || 'Unknown error'}
          </p>
        </div>
      ) : isLoading || isFetching ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : searchQuery && data ? (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Found {total} result{total === 1 ? '' : 's'} for "{data.data.query}"
              {activeCategoryName && (
                <span> in <span className="font-medium text-gray-700">{activeCategoryName}</span></span>
              )}
            </p>
          </div>

          {results.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="text-gray-500">No results found for "{searchQuery}"</p>
              <p className="text-sm text-gray-400 mt-1">Try different keywords{activeCategoryName ? ' or remove the category filter' : ''}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((result) => (
                <button
                  key={result.chunk_id}
                  type="button"
                  onClick={() => setActiveResult(result)}
                  className="card p-6 hover:shadow-md transition-shadow text-left w-full"
                >
                  <div className="flex gap-4">
                    {result.video.thumbnail_url && (
                      <div className="flex-shrink-0 relative">
                        <img
                          src={result.video.thumbnail_url}
                          alt=""
                          className="h-24 w-40 object-cover rounded"
                        />
                        <div className="absolute inset-0 flex items-center justify-center rounded bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
                          <svg className="h-10 w-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-900">
                          {result.video.title}
                        </p>
                        <Link
                          to={`/videos/${result.video.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-gray-400 hover:text-primary-600 whitespace-nowrap"
                        >
                          Details →
                        </Link>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                          {formatTimestamp(result.start_time)} - {formatTimestamp(result.end_time)}
                        </span>
                        {result.video.speaker && (
                          <span className="text-xs text-gray-500">{result.video.speaker}</span>
                        )}
                        <span className="text-xs text-gray-400">
                          Score: {result.relevance_score.toFixed(2)}
                        </span>
                      </div>
                      <p
                        className="mt-2 text-sm text-gray-700 line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: result.content }}
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">Search your content</h3>
          <p className="mt-2 text-sm text-gray-500">
            Enter a search query to find relevant content across all your video transcripts
          </p>
        </div>
      )}

      <VideoPlayerModal
        isOpen={activeResult !== null}
        onClose={() => setActiveResult(null)}
        source={activeResult?.video.source ?? ''}
        sourceId={activeResult?.video.source_id ?? null}
        sourceUrl={activeResult?.video.source_url ?? null}
        title={activeResult?.video.title ?? ''}
        startTime={activeResult?.start_time ?? 0}
      />
    </div>
  );
}
